import { useEffect, useMemo, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { fetchPublicPlanPrices } from '../api/pricing';

const BILLING_PERIODS = {
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
};

const PLAN_META = {
  DEMO: {
    displayName: 'Demo',
    description: 'Sistemi tanımak ve hızlı denemeler yapmak için baslangıç paketi.',
    highlight: false,
    cta: 'Ücretsiz Dene',
    features: [
      '1 deneme video hakki',
      '120 kredi / donem',
      'Standart islem onceligi',
      'Temel sablon erisimi',
    ],
  },
  PRO: {
    displayName: 'Pro',
    description: 'Bireysel ve aktif danışmanlar için ideal ana paket.',
    highlight: true,
    cta: 'Paketi Seç',
    features: [
      '6 video / ay cipasi',
      '1 video = 120 kredi donusumu',
      'AI destekli metin ve seslendirme',
      'Standart destek',
    ],
  },
  OFFICE: {
    displayName: 'Ofis',
    description: 'Ekip calışan ofisler ve yüksek hacimli kullanım için.',
    highlight: false,
    cta: 'İletişime Geç',
    features: [
      'Çoklu kullanıcı desteği',
      'Öncelikli render kuyruğu',
      'White Label add-on uyumluluğu',
      'Gelişmiş operasyon desteği',
    ],
  },
};

function normalizeCurrency(currency) {
  if (!currency || typeof currency !== 'string') {
    return 'TRY';
  }

  return currency.toUpperCase();
}

function formatPrice(amountMinor, currency) {
  if ((amountMinor ?? 0) <= 0) return 'Ücretsiz';

  const majorAmount = (amountMinor ?? 0) / 100;
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: normalizeCurrency(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(majorAmount);
}

function formatPeriodLabel(period) {
  return period === BILLING_PERIODS.YEARLY ? 'yillik' : 'aylik';
}

function mapPlanMeta(planCode, planName) {
  const byCode = PLAN_META[planCode];
  if (byCode) {
    return {
      ...byCode,
      displayName: planName || byCode.displayName,
    };
  }

  return {
    displayName: planName || planCode || 'Paket',
    description: 'Paket detayları için bizimle iletişime geçebilirsiniz.',
    highlight: false,
    cta: 'İletişime Geç',
    features: ['Plan ve kredi detaylari API üzerinden güncellenir.'],
  };
}

function groupPlans(planPrices) {
  const grouped = new Map();

  for (const item of planPrices) {
    if (!item || !item.planCode || !item.billingPeriod) continue;

    const planCode = String(item.planCode).toUpperCase();
    const billingPeriod = String(item.billingPeriod).toUpperCase();
    const existing = grouped.get(planCode);

    if (!existing) {
      grouped.set(planCode, {
        planCode,
        planName: item.planName,
        renderPriority: item.renderPriority ?? 255,
        prices: {
          [billingPeriod]: item,
        },
      });
      continue;
    }

    existing.planName = existing.planName || item.planName;
    existing.renderPriority = Math.min(existing.renderPriority, item.renderPriority ?? 255);
    existing.prices[billingPeriod] = item;
  }

  return Array.from(grouped.values()).sort((a, b) => {
    if (a.renderPriority !== b.renderPriority) {
      return a.renderPriority - b.renderPriority;
    }

    return a.planCode.localeCompare(b.planCode, 'tr');
  });
}

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState(BILLING_PERIODS.MONTHLY);
  const [planPrices, setPlanPrices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadPlanPrices = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const items = await fetchPublicPlanPrices();
        if (!isMounted) return;
        setPlanPrices(items);
      } catch (error) {
        if (!isMounted) return;
        setPlanPrices([]);
        setErrorMessage(error instanceof Error ? error.message : 'Paketler yuklenemedi.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPlanPrices();

    return () => {
      isMounted = false;
    };
  }, []);

  const visiblePlans = useMemo(() => {
    return groupPlans(planPrices)
      .map((plan) => {
        const selectedPricing =
          plan.prices[billingPeriod] ?? plan.prices[BILLING_PERIODS.MONTHLY] ?? null;

        if (!selectedPricing) {
          return null;
        }

        const meta = mapPlanMeta(plan.planCode, plan.planName);
        const isFallbackPricing =
          billingPeriod === BILLING_PERIODS.YEARLY && !plan.prices[BILLING_PERIODS.YEARLY];

        return {
          ...meta,
          code: plan.planCode,
          currentPricing: selectedPricing,
          isFallbackPricing,
        };
      })
      .filter(Boolean);
  }, [planPrices, billingPeriod]);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="section-padding">
      <div className="container-custom">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            Fiyatlandirma
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-slate-900 dark:text-white">
            Basit ve <span className="gradient-text">Şeffaf</span> Fiyatlandırma
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
            Aylık veya yıllık dönemi seçin, ihtiyacınıza uygun paketi net şekilde görün.
          </p>
        </div>

        <div className="flex items-center justify-center mb-12">
          <div className="inline-flex rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1.5">
            <button
              type="button"
              onClick={() => setBillingPeriod(BILLING_PERIODS.MONTHLY)}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-colors ${
                billingPeriod === BILLING_PERIODS.MONTHLY
                  ? 'bg-primary text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              Aylık
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod(BILLING_PERIODS.YEARLY)}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-colors ${
                billingPeriod === BILLING_PERIODS.YEARLY
                  ? 'bg-primary text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              Yıllık
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="max-w-6xl mx-auto rounded-2xl border border-slate-200 bg-white/80 dark:bg-slate-900/50 dark:border-slate-700 p-8 text-center text-slate-600 dark:text-slate-300">
            Paketler yukleniyor...
          </div>
        ) : null}

        {!isLoading && errorMessage ? (
          <div className="max-w-6xl mx-auto rounded-2xl border border-red-200 bg-red-50 text-red-700 dark:border-red-500/50 dark:bg-red-950/30 dark:text-red-200 p-6 text-center">
            {errorMessage}
          </div>
        ) : null}

        {!isLoading && !errorMessage ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {visiblePlans.map((plan) => (
              <div
                key={plan.code}
                className={`relative rounded-3xl p-8 flex flex-col ${
                  plan.highlight
                    ? 'bg-primary text-white scale-105 shadow-2xl z-10'
                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                }`}
              >
                {plan.highlight ? (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-white text-primary text-xs font-black uppercase tracking-wider shadow-lg">
                      <Sparkles className="w-3 h-3" />
                      En Cok Tercih Edilen
                    </span>
                  </div>
                ) : null}

                <div className="mb-6">
                  <h3 className={`text-xl font-bold mb-2 ${plan.highlight ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    {plan.displayName}
                  </h3>
                  <p className={`text-sm ${plan.highlight ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                    {plan.description}
                  </p>
                  {plan.isFallbackPricing ? (
                    <p className={`text-xs mt-2 ${plan.highlight ? 'text-white/75' : 'text-amber-600 dark:text-amber-400'}`}>
                      Bu paket icin yillik secenek su an acik degil.
                    </p>
                  ) : null}
                </div>

                <div className="mb-8">
                  <div className="flex items-end gap-2">
                    <span className={`text-5xl font-black ${plan.highlight ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                      {formatPrice(plan.currentPricing.amountMinor, plan.currentPricing.currency)}
                    </span>
                    <span className={`text-sm pb-2 ${plan.highlight ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                      / {formatPeriodLabel(plan.currentPricing.billingPeriod)}
                    </span>
                  </div>
                  <p className={`mt-2 text-sm ${plan.highlight ? 'text-white/90' : 'text-slate-600 dark:text-slate-400'}`}>
                    Dahil kredi:{' '}
                    <strong>{new Intl.NumberFormat('tr-TR').format(plan.currentPricing.includedCredits ?? 0)}</strong>
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.highlight ? 'text-white' : 'text-primary'}`} />
                      <span className={`text-sm ${plan.highlight ? 'text-white/90' : 'text-slate-600 dark:text-slate-400'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={scrollToContact}
                  className={`w-full py-3.5 rounded-xl font-bold transition-all duration-200 ${
                    plan.highlight
                      ? 'bg-white text-primary hover:bg-slate-50 shadow-lg'
                      : 'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <p className="text-center mt-12 text-slate-500 dark:text-slate-400 text-sm">
          Fiyatlar ve kredi paketleri admin panelde guncellendikce bu sayfaya otomatik yansitilir.
        </p>
      </div>
    </section>
  );
}
